import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, formatMoney } from "../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, UserPlus, Users, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const EMPTY = {
  name: "", phone: "", role: "", joining_date: "",
  wage_type: "monthly", monthly_salary: 0, daily_wage: 0,
  opening_balance: 0, active: true, notes: "",
};

export default function Employees() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/employees");
      setList(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setForm(EMPTY); setEditingId(null); setShowForm(true);
  };
  const startEdit = (e) => {
    setForm({
      name: e.name, phone: e.phone || "", role: e.role || "",
      joining_date: e.joining_date || "",
      wage_type: e.wage_type || "monthly",
      monthly_salary: e.monthly_salary || 0, daily_wage: e.daily_wage || 0,
      opening_balance: e.opening_balance || 0,
      active: e.active !== false, notes: e.notes || "",
    });
    setEditingId(e.id); setShowForm(true);
  };
  const cancel = () => { setShowForm(false); setEditingId(null); setForm(EMPTY); };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    const payload = {
      ...form,
      monthly_salary: Number(form.monthly_salary || 0),
      daily_wage: Number(form.daily_wage || 0),
      opening_balance: Number(form.opening_balance || 0),
    };
    try {
      if (editingId) {
        await api.patch(`/employees/${editingId}`, payload);
        toast.success("Employee updated");
      } else {
        await api.post("/employees", payload);
        toast.success("Employee added");
      }
      cancel(); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete ${e.name}? Their attendance records will remain but no new entries can be added.`)) return;
    try {
      await api.delete(`/employees/${e.id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Users size={26} strokeWidth={2.5} />
          <div>
            <h1 className="font-heading font-black text-2xl sm:text-3xl tracking-tight">Employees</h1>
            <p className="text-xs text-gray-500">Set salary or daily wage to auto-calculate monthly payable</p>
          </div>
        </div>
        {isAdmin && !showForm && (
          <button onClick={startNew} className="btn-primary h-10 flex items-center gap-2" data-testid="add-employee-btn">
            <UserPlus size={16} /> Add Employee
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={submit} className="card-flat p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="employee-form">
          <div>
            <label className="small-label block mb-1">Name *</label>
            <input className="input-lg" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="emp-name" />
          </div>
          <div>
            <label className="small-label block mb-1">Phone</label>
            <input className="input-lg" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="emp-phone" />
          </div>
          <div>
            <label className="small-label block mb-1">Role</label>
            <input className="input-lg" placeholder="Driver / Helper / Manager…" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="emp-role" />
          </div>
          <div>
            <label className="small-label block mb-1">Joining Date</label>
            <input type="date" className="input-lg" value={form.joining_date} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} data-testid="emp-joining" />
          </div>
          <div>
            <label className="small-label block mb-1">Wage Type *</label>
            <select className="input-lg" value={form.wage_type} onChange={(e) => setForm({ ...form, wage_type: e.target.value })} data-testid="emp-wage-type">
              <option value="monthly">Monthly Salary</option>
              <option value="daily">Daily Wage</option>
            </select>
          </div>
          {form.wage_type === "monthly" ? (
            <div>
              <label className="small-label block mb-1">Monthly Salary (₹)</label>
              <input type="number" className="input-lg data-num text-right" value={form.monthly_salary} onChange={(e) => setForm({ ...form, monthly_salary: e.target.value })} data-testid="emp-monthly-salary" />
            </div>
          ) : (
            <div>
              <label className="small-label block mb-1">Daily Wage (₹)</label>
              <input type="number" className="input-lg data-num text-right" value={form.daily_wage} onChange={(e) => setForm({ ...form, daily_wage: e.target.value })} data-testid="emp-daily-wage" />
            </div>
          )}
          <div>
            <label className="small-label block mb-1">Opening Advance (₹)</label>
            <input type="number" step="0.01" className="input-lg data-num text-right"
              value={form.opening_balance}
              onChange={(e) => setForm({ ...form, opening_balance: e.target.value })}
              placeholder="Advance paid before tracking started"
              data-testid="emp-opening" />
          </div>
          <div className="sm:col-span-2 lg:col-span-3">
            <label className="small-label block mb-1">Notes</label>
            <input className="input-lg" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="emp-notes" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="emp-active" />
            <label htmlFor="active" className="text-sm font-semibold">Active (show in attendance)</label>
          </div>
          <div className="sm:col-span-2 lg:col-span-3 flex justify-end gap-2">
            <button type="button" onClick={cancel} className="h-11 px-5 border border-gray-300 rounded-sm font-semibold text-sm" data-testid="emp-cancel">Cancel</button>
            <button type="submit" className="btn-primary h-11 px-6" data-testid="emp-save">{editingId ? "Update" : "Add Employee"}</button>
          </div>
        </form>
      )}

      <div className="card-flat">
        <div className="overflow-auto">
          <table className="table-dense w-full">
            <thead>
              <tr>
                <th className="text-left">Name</th>
                <th className="text-left">Role</th>
                <th className="text-left">Phone</th>
                <th className="text-left">Wage</th>
                <th className="num">Amount</th>
                <th className="text-left">Status</th>
                {isAdmin && <th className="text-right">Action</th>}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="7" className="py-6 text-center text-gray-400">Loading…</td></tr>}
              {!loading && list.length === 0 && (
                <tr><td colSpan="7" className="py-10 text-center text-gray-400 text-sm">
                  No employees yet. Click <strong>Add Employee</strong> to get started.
                </td></tr>
              )}
              {list.map((e) => (
                <tr key={e.id} data-testid={`emp-row-${e.id}`}>
                  <td className="font-semibold">{e.name}</td>
                  <td className="text-gray-600">{e.role || "—"}</td>
                  <td className="data-num text-xs">{e.phone || "—"}</td>
                  <td className="capitalize text-xs">{e.wage_type}</td>
                  <td className="num">
                    {e.wage_type === "monthly"
                      ? `₹ ${formatMoney(e.monthly_salary)} /mo`
                      : `₹ ${formatMoney(e.daily_wage)} /day`}
                  </td>
                  <td>
                    {e.active ? (
                      <span className="text-xs font-bold text-[#15803D]">ACTIVE</span>
                    ) : (
                      <span className="text-xs font-bold text-gray-400">INACTIVE</span>
                    )}
                  </td>
                  {isAdmin && (
                    <td className="text-right whitespace-nowrap">
                      <Link to={`/employees/${e.id}/ledger`}
                        className="text-xs font-semibold text-gray-600 hover:text-black inline-flex items-center gap-1 mr-3"
                        data-testid={`emp-ledger-${e.id}`}>
                        Ledger <ArrowRight size={12} />
                      </Link>
                      <Link to={`/employees/${e.id}/food-ledger`}
                        className="text-xs font-semibold text-[#B45309] hover:text-black inline-flex items-center gap-1 mr-3"
                        data-testid={`emp-food-ledger-${e.id}`}>
                        Food <ArrowRight size={12} />
                      </Link>
                      <button onClick={() => startEdit(e)} className="text-xs font-semibold text-gray-600 hover:text-black inline-flex items-center gap-1 mr-3" data-testid={`emp-edit-${e.id}`}>
                        <Pencil size={12} /> Edit
                      </button>
                      <button onClick={() => remove(e)} className="text-xs font-semibold text-[#B91C1C] hover:text-black inline-flex items-center gap-1" data-testid={`emp-del-${e.id}`}>
                        <Trash2 size={12} /> Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
